import React, { useEffect } from 'react';
import { useFormContext, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { IconButton } from '@mui/material';
import { RateInput } from 'components/template/TemplateForm';
import { fetchLanguages } from 'slices/languageSlice';
import toast from 'react-hot-toast';

const CustomTranslationTiers = ({ item, hasTemplateSelected }) => {
  const dispatch = useDispatch();
  const { control, formState: { errors } } = useFormContext();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'customTranslationTiers',
  });

  // Live watch of all tiers' data — needed to compute cross-tier exclusivity in real time
  const watchedTiers = useWatch({ control, name: 'customTranslationTiers' }) || [];

  const getSelectedLanguageIds = (excludeIndex) =>
    watchedTiers
      .filter((_, idx) => idx !== excludeIndex)
      .flatMap((tier) => (tier?.language_ids || []).map((lang) => lang?.value ?? lang))
      .filter(Boolean);

  // Prefill from item.custom_translation_tiers (edit mode)
  useEffect(() => {
    if (!item?.custom_translation_tiers?.length) return;

    const blocks = item.custom_translation_tiers.map((tier) => {
      const languageOptions = (tier.languages || []).map((l) => ({
        value: l.language_id ?? l.language?.id,
        label: l.language?.name || `Language ${l.language_id}`,
      }));
      return {
        language_ids: languageOptions,
        translation_rate: tier.translation_rate ?? 0,
        editing_rate: tier.editing_rate ?? 0,
        dtp_rate: tier.dtp_rate ?? 0,
        review_rate: tier.review_rate ?? 0,
        audio_recording_rate: tier.audio_recording_rate ?? 0,
        transcription_rate: tier.transcription_rate ?? 0,
      };
    });

    replace(blocks);
  }, [item?.custom_translation_tiers, replace]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }, currentIndex) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        { pageLength: 20, sortBy: 'id', sortDirection: 'asc', search: inputValue },
        true,
        true
      )
    );

    const selectedElsewhere = getSelectedLanguageIds(currentIndex);

    return {
      options: (result?.data || [])
        .filter((language) => !selectedElsewhere.includes(language.id))
        .map((language) => ({
          value: language.id,
          label: language.name,
        })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const handleAddTier = () => {
    append({
      language_ids: [],
      translation_rate: 0,
      editing_rate: 0,
      dtp_rate: 0,
      review_rate: 0,
      audio_recording_rate: 0,
      transcription_rate: 0,
    });
  };

  if (!hasTemplateSelected) return null;

  return (
    <div className="w-100 mt-4">
      {/* <div className="row-primary row-r justify-content-between flex-row">
        <h2 className="fst-italic">Custom Translation Tiers</h2>
      </div> */}

      {fields.map((block, index) => {
        const selectedElsewhere = getSelectedLanguageIds(index);

        const inputError = errors?.customTranslationTiers?.[index]?.language_ids;

        return (
          <div
            key={block.id}
            className="row-primary rate_container_outer mt-3"
            style={{ flexDirection: 'column', paddingBottom: 15 }}
          >
            <div className="row-primary align-items-center">
              <h2>Select Languages *</h2>
              <div style={{ width: '100%' }}>
                <Controller
                  name={`customTranslationTiers.${index}.language_ids`}
                  control={control}
                  rules={{ required: "Languages must be specified" }}
                  render={({ field }) => (
                    <AsyncPaginate
                      styles={selectCustomStyles}
                      isMulti
                      menuPlacement="auto"
                      value={field.value}
                      // filterOption re-filters ALREADY LOADED/cached options on every render,
                      // so jaise hi doosray tier mein language select hoti hai, ye turant list se hat jati hai
                      filterOption={(option) => !selectedElsewhere.includes(option.value)}
                      loadOptions={(inputValue, loadedOptions, additional) =>
                        filteredLanguagesLoadOptions(inputValue, loadedOptions, additional, index)
                      }
                      cacheUniqs={[watchedTiers.map((t) => (t?.language_ids || []).map((l) => l?.value ?? l).join(',')).join('|')]}
                      isClearable
                      placeholder="Select languages"
                      onChange={(selected) => {
                        const selectedValues = (selected || []).map((s) => s.value);
                        const clash = selectedValues.some((v) => selectedElsewhere.includes(v));
                        if (clash) {
                          toast.error('This language is already used in another tier.');
                          return;
                        }
                        field.onChange(selected || []);
                      }}
                      additional={{ page: 1 }}
                    />
                  )}
                />
              </div>
                {inputError && (
                    <span className="error-message">
                    {inputError.message}
                    </span>
                )}
              <IconButton color="error" onClick={() => remove(index)}>
                <i className="fa-solid fa-trash-can trash_icon"></i>
              </IconButton>
            </div>

            <div className="rate_container_wrapper">
              <div className="template_create rate_container">
                <div className="row-primary align-items-end inner">
                  <h2 className="mb-3 my-0">Rates</h2>

                  <div className="Inter_rate_row">
                    <h4>Translation</h4>
                    <span className="static-unit-text">per word</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.translation_rate`} placeholder="Translation rate" currency_symbol={true} />
                    </div>
                  </div>

                  <div className="Inter_rate_row">
                    <h4>Editing</h4>
                    <span className="static-unit-text">per word</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.editing_rate`} placeholder="Editing rate" currency_symbol={true} />
                    </div>
                  </div>

                  <div className="Inter_rate_row">
                    <h4>DTP</h4>
                    <span className="static-unit-text">per work</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.dtp_rate`} placeholder="DTP rate" currency_symbol={true} />
                    </div>
                  </div>
                </div>

                <div className="row-primary align-items-end inner">
                  <h2 className="mb-3 my-0">Rates</h2>

                  <div className="Inter_rate_row">
                    <h4>Review</h4>
                    <span className="static-unit-text">per page</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.review_rate`} placeholder="Review rate" currency_symbol={true} />
                    </div>
                  </div>

                  <div className="Inter_rate_row">
                    <h4>Audio Recording</h4>
                    <span className="static-unit-text">per word</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.audio_recording_rate`} placeholder="Audio Recording rate" currency_symbol={true} />
                    </div>
                  </div>

                  <div className="Inter_rate_row">
                    <h4>Transcription</h4>
                    <span className="static-unit-text">per audio minute</span>
                    <div className="create inter_wrapper translation_rate_input">
                      <RateInput control={control} name={`customTranslationTiers.${index}.transcription_rate`} placeholder="Transcription rate" currency_symbol={true} />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="d-flex justify-content-start mt-3">
        <SubmitButton btnText="Add Custom Tier" buttonType="button" handleSubmit={handleAddTier} />
      </div>
    </div>
  );
};

export default CustomTranslationTiers;